/**
 * RepeatableGroup — renders a list_input_* variable as a set of collapsible
 * entry rows. Each entry uses VariableInputForm's entryControls mode: the
 * chevron and trash render inline on the first field row with no wrapper box.
 *
 * Outer InputGroup header: group title + Expand All toggle + New button.
 * Entries separated by a thin divider. All collapsed by default; new entries
 * open expanded (#9).
 *
 * No drag-to-reorder.
 */
import React, { useRef, useState } from "react";
import { v4 as uuid } from "uuid";
import styled from "styled-components";
import { Button, InputGroup } from "@cinesys/ui/components";
import { Maximize2, Minimize2, Plus } from "react-feather";
import VariableInputForm from "./VariableInputForm";

const OuterWrapper = styled.div`
  margin: 0 15px;
`;

const EntriesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: 5px;
`;

const EntryBox = styled.div`
  border: 1px solid var(--dark500);
  border-radius: 10px;
  overflow: hidden;
`;

interface Props {
  listInput: ListInputConfig;
  /** Title shown on the outer non-collapsible InputGroup. */
  groupTitle?: string;
  value: ListInputInstance[];
  onChange: (value: ListInputInstance[]) => void;
  systemDomainId?: string;
  config?: PluginConfigItem | ScriptConfigItem | ListInputConfig;
  required?: boolean;
}

/**
 * Derive the display label for a collapsed entry.
 * Uses the first VariableInstance in the entry that has a non-empty primitive value.
 * Falls back to "Entry N" (1-based).
 */
export const entryHeader = (
  entry: ListInputInstance,
  index: number
): string => {
  for (const vi of entry.variables) {
    const v = vi.value;
    if (v === null || v === undefined || Array.isArray(v) || v === false) {
      continue;
    }
    const s = String(v).trim();
    if (s !== "") {
      return s;
    }
  }
  return `Entry ${index + 1}`;
};

const RepeatableGroup = ({
  listInput,
  groupTitle,
  value,
  onChange,
  systemDomainId: _systemDomainId,
  config: _config,
  required: _required,
}: Props) => {
  const safeValue = Array.isArray(value) ? value : [];

  const stableKeys = useRef<string[]>([]);
  while (stableKeys.current.length < safeValue.length) {
    stableKeys.current.push(uuid());
  }

  const [collapsedMap, setCollapsedMap] = useState<Record<number, boolean>>(
    () => Object.fromEntries(safeValue.map((_, i) => [i, true]))
  );

  const setCollapsed = (index: number, c: boolean) =>
    setCollapsedMap((prev) => ({ ...prev, [index]: c }));

  const isCollapsed = (index: number) => collapsedMap[index] ?? true;

  const allExpanded =
    safeValue.length > 0 && safeValue.every((_, i) => !isCollapsed(i));

  const toggleExpandAll = () => {
    const target = allExpanded;
    setCollapsedMap(Object.fromEntries(safeValue.map((_, i) => [i, target])));
  };

  const addEntry = () => {
    const newEntry: ListInputInstance = {
      listInputConfigId: listInput.id,
      variables: [],
    };
    const nextIndex = safeValue.length;
    stableKeys.current.push(uuid());
    setCollapsedMap((prev) => ({ ...prev, [nextIndex]: false }));
    onChange([...safeValue, newEntry]);
  };

  const removeAt = (index: number) => {
    const next = safeValue.filter((_, i) => i !== index);
    stableKeys.current.splice(index, 1);
    const nextMap: Record<number, boolean> = {};
    Object.entries(collapsedMap).forEach(([k, v]) => {
      const ki = Number(k);
      if (ki < index) {
        nextMap[ki] = v;
      } else if (ki > index) {
        nextMap[ki - 1] = v;
      }
    });
    setCollapsedMap(nextMap);
    onChange(next);
  };

  const updateAt = (index: number, updated: ListInputInstance) => {
    onChange(safeValue.map((e, i) => (i === index ? updated : e)));
  };

  const outerButtons = (
    <>
      {safeValue.length > 0 && (
        <Button
          icon={allExpanded ? Minimize2 : Maximize2}
          label={allExpanded ? "Collapse All" : "Expand All"}
          type="default"
          borderless
          onClick={toggleExpandAll}
        />
      )}
      <Button
        icon={Plus}
        label="New"
        type="success"
        borderless
        onClick={addEntry}
      />
    </>
  );

  return (
    <OuterWrapper>
      <InputGroup
        title={groupTitle ?? listInput.title}
        noHover
        noPad
        buttons={outerButtons}
      >
        {safeValue.length > 0 && (
          <EntriesWrapper>
            {safeValue.map((entry, index) => (
              <EntryBox key={stableKeys.current[index]}>
                <VariableInputForm
                  value={entry}
                  config={listInput}
                  onChange={(updated) =>
                    updateAt(index, updated as ListInputInstance)
                  }
                  noMargin
                  isCollapsed={false}
                  entryControls={{
                    collapsed: isCollapsed(index),
                    onCollapse: (c) => setCollapsed(index, c),
                    onDelete: () => removeAt(index),
                  }}
                />
              </EntryBox>
            ))}
          </EntriesWrapper>
        )}
      </InputGroup>
    </OuterWrapper>
  );
};

export default RepeatableGroup;
